import { BrokerSubscriptionTypesEnum } from "dash-common";
import camelCase from "lodash/camelCase";
import { ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Button,
  DropdownOptions,
  Flex,
  ModalProvider,
  Tabs,
  TooltipPopup,
  ViewerContext,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";
import { useLocation, useNavigate } from "react-router-dom";

import { getCurrentTabFromSearchParam } from "common/helpers/route.helper";
import { RoutesPath } from "flow-components/routes/routes";

import Container from "flow-components/Container/Container";
import Footer from "flow-components/Footer/Footer";
import Header from "flow-components/Header/Header";
import { PROFILE_DEFAULT_TAB, ProfileTabsKeys } from "./ProfilePage.constants";
import { ProfileContainer, TabsWrapper } from "./ProfilePage.styles";
import AccountInfo from "./components/AccountInfo/AccountInfo";
import ProfileBilling from "./components/ProfileBilling/ProfileBilling";
import ProfileSettings from "./components/ProfileSettings/ProfileSettings";

function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = getCurrentTabFromSearchParam(
    location.search
  ) as ProfileTabsKeys;
  const { broker } = useContext(ViewerContext);
  const activeTab = currentTab || PROFILE_DEFAULT_TAB;
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const { t: tProfileLabels } = useTranslation("pages", {
    keyPrefix: "profilePage.labels",
  });

  const isDemoBroker =
    broker?.subscriptionType === BrokerSubscriptionTypesEnum.DEMO;

  const tabsMap: Record<ProfileTabsKeys, () => ReactNode> = {
    [ProfileTabsKeys.ACCOUNT_INFO]: () => <AccountInfo />,
    [ProfileTabsKeys.BILLING]: () => <ProfileBilling />,
    [ProfileTabsKeys.SETTINGS]: () => (
      <ProfileSettings isSubscriptionActive={isSubscriptionActive} />
    ),
  };

  return (
    <ModalProvider>
      <>
        <DropdownOptions />
        <TooltipPopup />
        <Box h="100%" className="bg-slate-50">
          <Header />
          <Container
            withBreadcrumbs={false}
            showContent={true}
            withSidebar={!!isSubscriptionActive}>
            <div className="flex-col w-full">
              {!isSubscriptionActive && (
                <Button
                  variation="outline"
                  onClick={() =>
                    navigate(RoutesPath.CompleteSignUpPickSubscription)
                  }>
                  &lt; Choose a plan
                </Button>
              )}

              <ProfileContainer
                alignSelf="stretch"
                w="100%"
                h="fit-content"
                flexDirection="column"
                px={[4, 6, 6, 6]}
                py={[4, 10, 10, 10]}
                mt={6}>
                <TabsWrapper w="100%">
                  <Tabs
                    adaptiveFullWidthTabs={true}
                    variation="secondary"
                    tabs={Object.values(ProfileTabsKeys).map((key) => ({
                      key,
                      label: tProfileLabels(camelCase(key)),
                      disabled:
                        (key === ProfileTabsKeys.BILLING &&
                          (isDemoBroker || !isSubscriptionActive)) ||
                        (key === ProfileTabsKeys.SETTINGS &&
                          !isSubscriptionActive),
                    }))}
                    activeTab={activeTab}
                    onClick={(tab) =>
                      navigate(`${RoutesPath.Profile}?tab=${tab}`)
                    }
                    w="100%"
                  />
                </TabsWrapper>
                <Flex
                  flexDirection="column"
                  flex={1}
                  mt={5}
                  w="100%"
                  justifyContent="center">
                  {tabsMap[activeTab]() || null}
                </Flex>
              </ProfileContainer>
            </div>
          </Container>
          <Footer variation="light" />
        </Box>
      </>
    </ModalProvider>
  );
}

export default ProfilePage;
